import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Order {
  id: string;
  customer: string;
  weddingDate: string;
  venue: string;
  service: string;
  amount: string;
  status: string;
  orderDate: string;
  notes: string;
  files: string[];
}

export default function VendorPortal() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showReverseOrderModal, setShowReverseOrderModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showTaxModal, setShowTaxModal] = useState(false);

  const menuItems = [
    { id: 'dashboard', icon: 'ri-dashboard-line', label: '대시보드', path: '/vendor-portal' },
    { id: 'orders', icon: 'ri-file-list-3-line', label: '발주서 관리', path: '/vendor-portal/orders' },
    { id: 'schedule', icon: 'ri-calendar-line', label: '촬영 일정 관리', path: '/vendor-portal/studio-schedule' },
    { id: 'ordersheet', icon: 'ri-file-text-line', label: '촬영 오더시트', path: '/vendor-portal/studio-customers' },
    { id: 'staff', icon: 'ri-team-line', label: '작가·스태프 배정', path: '/vendor-portal/studio-staff' },
    { id: 'photos', icon: 'ri-folder-image-line', label: '촬영 사진 관리', path: '/vendor-portal/studio-photos' },
    { id: 'collaboration', icon: 'ri-user-star-line', label: '플래너 협업 관리', path: '/vendor-portal/studio-requests' },
    { id: 'invoice', icon: 'ri-file-text-line', label: '인보이스 관리', path: '/vendor-portal/invoice' },
    { id: 'tax', icon: 'ri-file-list-line', label: '세금계산서 관리', path: '/vendor-portal/tax' },
    { id: 'products', icon: 'ri-shopping-bag-line', label: '상품 관리', path: '/vendor-portal/products' }
  ];

  const orders: Order[] = [
    {
      id: 'ORD-001',
      customer: '김민수',
      weddingDate: '2024-03-15',
      venue: '그랜드볼룸',
      service: '스튜디오',
      amount: '1,500,000원',
      status: '발주서',
      orderDate: '2024-01-15',
      notes: '고객 요청사항: 야외촬영 포함',
      files: ['계약서.pdf', '촬영컨셉.jpg']
    },
    {
      id: 'ORD-002',
      customer: '박지영',
      weddingDate: '2024-04-20',
      venue: '로얄팰리스',
      service: '드레스',
      amount: '800,000원',
      status: '역발주',
      orderDate: '2024-01-20',
      notes: '',
      files: ['드레스사진.jpg']
    },
    {
      id: 'ORD-003',
      customer: '정현우',
      weddingDate: '2024-05-10',
      venue: '시그니엘',
      service: '메이크업',
      amount: '600,000원',
      status: '인보이스',
      orderDate: '2024-02-01',
      notes: '리허설 메이크업 포함',
      files: []
    }
  ];

  const handleReverseOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowReverseOrderModal(true);
  };

  const handleCreateInvoice = (order: Order) => {
    setSelectedOrder(order);
    setShowInvoiceModal(true);
  };

  const handleCreateTaxInvoice = (order: Order) => {
    setSelectedOrder(order);
    setShowTaxModal(true);
  };

  const handleMenuClick = (item: typeof menuItems[0]) => {
    if (item.path.startsWith('/vendor-portal/studio-')) {
      // Navigate to vendor portal studio pages
      navigate(item.path);
    } else if (item.path === '/vendor-portal') {
      // Stay in vendor portal dashboard
      setCurrentPage('dashboard');
    } else {
      // Stay in vendor portal for other pages
      setCurrentPage(item.id);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '발주서': return 'bg-blue-100 text-blue-800';
      case '역발주': return 'bg-yellow-100 text-yellow-800';
      case '인보이스': return 'bg-orange-100 text-orange-800';
      case '결제완료': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderContent = () => {
    switch(currentPage) {
      case 'dashboard':
        return (
          <div>
            {/* 통계 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">신규 발주</p>
                    <p className="text-2xl font-bold text-gray-900">8건</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">이번 주 촬영</p>
                    <p className="text-2xl font-bold text-gray-900">12건</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-calendar-line text-2xl text-green-600"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">이번 달 매출</p>
                    <p className="text-2xl font-bold text-gray-900">₩24,500,000</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-2xl text-purple-600"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">완료된 거래</p>
                    <p className="text-2xl font-bold text-gray-900">156건</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-2xl text-orange-600"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* 최근 발주 목록 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">최근 발주 내역</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">발주번호</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">고객명</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상품명</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">촬영일</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">금액</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[
                      { id: 'PO-2024-001', customer: '김민수·이지은', product: '스튜디오 토탈 30P', date: '2024-02-15', amount: '₩1,200,000', status: '확인대기' },
                      { id: 'PO-2024-002', customer: '박준호·최수진', product: '스튜디오 프리미엄 50P', date: '2024-02-18', amount: '₩1,800,000', status: '진행중' },
                      { id: 'PO-2024-003', customer: '정우성·한지민', product: '스튜디오 베이직 20P', date: '2024-02-20', amount: '₩900,000', status: '확인완료' }
                    ].map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50 cursor-pointer">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{order.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.customer}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.product}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{order.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.amount}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                            order.status === '확인대기' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === '진행중' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 이번 주 촬영 일정 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">이번 주 촬영 일정</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {[
                    { date: '2024-02-15 (목)', time: '10:00', customer: '김민수·이지은', type: '리허설', location: '스튜디오 A' },
                    { date: '2024-02-16 (금)', time: '14:00', customer: '박준호·최수진', type: '본식', location: '스튜디오 B' },
                    { date: '2024-02-17 (토)', time: '11:00', customer: '정우성·한지민', type: '리허설', location: '스튜디오 A' }
                  ].map((schedule, index) => (
                    <div key={index} className="flex items-center p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                        <i className="ri-camera-line text-2xl text-blue-600"></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="text-sm font-semibold text-gray-900">{schedule.customer}</h3>
                          <span className="text-xs text-gray-500">{schedule.location}</span>
                        </div>
                        <p className="text-sm text-gray-600">{schedule.type} 촬영</p>
                        <p className="text-xs text-gray-500 mt-1">{schedule.date} {schedule.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'orders':
        return (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-file-text-line text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{order.id}</h3>
                      <p className="text-sm text-gray-600">{order.customer} · {order.service}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">본식일</p>
                    <p className="text-sm font-medium text-gray-900">{order.weddingDate}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">예식장</p>
                    <p className="text-sm font-medium text-gray-900">{order.venue}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">계약금액</p>
                    <p className="text-sm font-medium text-gray-900">{order.amount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">발주일</p>
                    <p className="text-sm font-medium text-gray-900">{order.orderDate}</p>
                  </div>
                </div>

                {order.files && order.files.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">첨부파일</p>
                    <div className="flex flex-wrap gap-2">
                      {order.files.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-1 rounded-lg">
                          <i className="ri-file-line text-gray-400"></i>
                          <span className="text-sm text-gray-700">{file}</span>
                          <button className="text-blue-600 hover:text-blue-800 cursor-pointer">
                            <i className="ri-download-line text-sm"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {order.notes && (
                  <div className="mb-4 bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">특이사항</p>
                    <p className="text-sm text-gray-700">{order.notes}</p>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                  {order.status === '발주서' && (
                    <button
                      onClick={() => handleReverseOrder(order)}
                      className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      역발주 작성
                    </button>
                  )}
                  {order.status === '역발주' && (
                    <button
                      onClick={() => handleCreateInvoice(order)}
                      className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      인보이스 발행
                    </button>
                  )}
                  {order.status === '결제완료' && (
                    <button
                      onClick={() => handleCreateTaxInvoice(order)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      세금계산서 발행
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      case 'invoice':
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">인보이스 관리</h2>
              <p className="text-sm text-gray-600 mt-1">청구서를 발행하고 관리할 수 있습니다</p>
            </div>
            <div className="p-6">
              <p className="text-gray-600">인보이스 관리 기능이 곧 제공됩니다.</p>
            </div>
          </div>
        );

      case 'tax':
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">세금계산서 관리</h2>
              <p className="text-sm text-gray-600 mt-1">세금계산서를 발행하고 관리할 수 있습니다</p>
            </div>
            <div className="p-6">
              <p className="text-gray-600">세금계산서 관리 기능이 곧 제공됩니다.</p>
            </div>
          </div>
        );

      case 'products':
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">상품 관리</h2>
              <p className="text-sm text-gray-600 mt-1">제공하는 상품과 패키지를 관리할 수 있습니다</p>
            </div>
            <div className="p-6">
              <p className="text-gray-600">상품 관리 기능이 곧 제공됩니다.</p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* 사이드바 */}
      <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}>
        {/* 로고 영역 */}
        <div className="p-6 border-b border-gray-200">
          {isSidebarOpen ? (
            <div>
              <h1 className="text-xl font-bold text-gray-800">메이스튜디오</h1>
              <p className="text-sm text-gray-600 mt-1">업체 포털</p>
            </div>
          ) : (
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">M</span>
            </div>
          )}
        </div>

        {/* 메뉴 */}
        <nav className="flex-1 overflow-y-auto py-4">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item)}
              className={`w-full flex items-center px-6 py-3 text-left hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer ${
                currentPage === item.id ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' : 'text-gray-700'
              }`}
            >
              <i className={`${item.icon} text-lg ${isSidebarOpen ? 'mr-3' : ''}`}></i>
              {isSidebarOpen && <span>{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* 하단 사용자 정보 */}
        <div className="p-4 border-t border-gray-200">
          {isSidebarOpen ? (
            <div>
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-user-line text-gray-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">김대표</p>
                  <p className="text-xs text-gray-600">메이스튜디오</p>
                </div>
              </div>
              <button className="w-full px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap cursor-pointer">
                <i className="ri-logout-box-line mr-2"></i>로그아웃
              </button>
            </div>
          ) : (
            <button className="w-full p-2 text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer">
              <i className="ri-logout-box-line text-lg"></i>
            </button>
          )}
        </div>
      </aside>

      {/* 메인 컨텐츠 */}
      <div className="flex-1 flex flex-col">
        {/* 헤더 */}
        <header className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="mr-4 p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
              <h2 className="text-xl font-semibold text-gray-900">
                {menuItems.find(item => item.id === currentPage)?.label || '대시보드'}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-notification-line text-xl"></i>
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </header>

        {/* 컨텐츠 영역 */}
        <main className="flex-1 overflow-y-auto p-8">
          {renderContent()}
        </main>
      </div>

      {/* 역발주 작성 모달 */}
      {showReverseOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">역발주 작성</h2>
              <button 
                onClick={() => setShowReverseOrderModal(false)}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">발주 정보</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">발주번호:</span>
                    <span className="ml-2 font-medium">{selectedOrder.id}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">고객명:</span>
                    <span className="ml-2 font-medium">{selectedOrder.customer}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">서비스:</span>
                    <span className="ml-2 font-medium">{selectedOrder.service}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">본식일:</span>
                    <span className="ml-2 font-medium">{selectedOrder.weddingDate}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">확정 금액</label>
                <input 
                  type="text" 
                  defaultValue={selectedOrder.amount}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">서비스 일정</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">시작일</label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">완료일</label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">서비스 상세 내용</label>
                <textarea 
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="제공할 서비스의 상세 내용을 입력하세요"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">추가 요청사항</label>
                <textarea 
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="추가 요청사항이나 변경사항을 입력하세요"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button 
                onClick={() => setShowReverseOrderModal(false)}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button 
                className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                역발주 전송
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 인보이스 발행 모달 */}
      {showInvoiceModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">인보이스 발행</h2>
              <button 
                onClick={() => setShowInvoiceModal(false)}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-orange-50 rounded-lg p-4">
                <h3 className="font-semibold text-orange-900 mb-2">발주 정보</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-orange-700">발주번호:</span>
                    <span className="ml-2 font-medium">{selectedOrder.id}</span>
                  </div>
                  <div>
                    <span className="text-orange-700">고객명:</span>
                    <span className="ml-2 font-medium">{selectedOrder.customer}</span>
                  </div>
                  <div>
                    <span className="text-orange-700">서비스:</span>
                    <span className="ml-2 font-medium">{selectedOrder.service}</span>
                  </div>
                  <div>
                    <span className="text-orange-700">계약금액:</span>
                    <span className="ml-2 font-medium">{selectedOrder.amount}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">인보이스 번호</label>
                <input 
                  type="text" 
                  placeholder="INV-2024-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">청구 금액</label>
                <input 
                  type="text" 
                  defaultValue={selectedOrder.amount}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">결제 기한</label>
                <input 
                  type="date" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">계좌 정보</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">은행명</label>
                    <input 
                      type="text" 
                      placeholder="국민은행"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">계좌번호</label>
                    <input 
                      type="text" 
                      placeholder="123-456-789012"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">비고</label>
                <textarea 
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="추가 안내사항을 입력하세요"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button 
                onClick={() => setShowInvoiceModal(false)}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button 
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                인보이스 발행
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 세금계산서 발행 모달 */}
      {showTaxModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">세금계산서 발행</h2>
              <button 
                onClick={() => setShowTaxModal(false)}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">발주 정보</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">발주번호:</span>
                    <span className="ml-2 font-medium">{selectedOrder.id}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">고객명:</span>
                    <span className="ml-2 font-medium">{selectedOrder.customer}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">서비스:</span>
                    <span className="ml-2 font-medium">{selectedOrder.service}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">계약금액:</span>
                    <span className="ml-2 font-medium">{selectedOrder.amount}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">세금계산서 번호</label>
                <input 
                  type="text" 
                  placeholder="TAX-2024-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">공급가액</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">부가세</label>
                  <input 
                    type="text" 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">발행일</label>
                <input 
                  type="date" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">사업자등록번호</label>
                <input 
                  type="text" 
                  placeholder="123-45-67890"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">비고</label>
                <textarea 
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="추가 안내사항을 입력하세요"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button 
                onClick={() => setShowTaxModal(false)}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                취소
              </button>
              <button 
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                세금계산서 발행
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
